import { useEffect, useRef } from "react";
import { Button } from "app/components/ui/Button/Button";
import { QUOTATION_SESSION_EXPIRED_TIMEOUT } from "app/constants";
import { useIntl } from "react-intl";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

export const useSessionTimeout = ({
	isSDPQuote,
	isStopoverQuote,
	saveQuotationSearchQueryString,
	showAlert,
}) => {
	const intl = useIntl();
	const { track } = useAnalytics();

	// Keep the latest callbacks in a ref so the timeout below is established once (when the quote
	// type changes) instead of on every render. showAlert (from withAlert) and track (from
	// useAnalytics) receive a new identity on each render; when they were listed in the effect
	// deps, the 10-minute timer was cleared and reset continuously. Worse, once the alert opened
	// its own re-render reset the timer again — under fake timers (cy.tick) that fires the callback
	// in a loop and thrashes the alert modal, and in production it could indefinitely postpone the
	// session-expired alert.
	const handlersRef = useRef();
	handlersRef.current = { intl, track, saveQuotationSearchQueryString, showAlert };

	useEffect(() => {
		if (isSDPQuote || isStopoverQuote) {
			return undefined;
		}

		const timeoutID = setTimeout(() => {
			const {
				intl: currentIntl,
				track: currentTrack,
				saveQuotationSearchQueryString: currentSaveQuotationSearchQueryString,
				showAlert: currentShowAlert,
			} = handlersRef.current;

			const alertTitle = currentIntl.formatMessage({
				id: `quotation.alert.session.expired.title`,
			});
			const alertMessage = currentIntl.formatMessage({
				id: `quotation.alert.session.expired.message`,
			});

			const alertActionNodes = [
				<Button
					variant="primary"
					size="lg"
					key="quotation.alert.session.expired.button.label"
				>
					{currentIntl.formatMessage({
						id: `quotation.alert.session.expired.button.label`,
					})}
				</Button>,
			];

			const onConfirm = () => {
				window.location.reload();
			};

			currentSaveQuotationSearchQueryString("");
			currentShowAlert({ alertTitle, alertMessage, alertActionNodes, onConfirm });
			currentTrack("quotation_session_expired_timeout");
		}, QUOTATION_SESSION_EXPIRED_TIMEOUT);

		return () => {
			clearTimeout(timeoutID);
		};
	}, [isSDPQuote, isStopoverQuote]);
};
